import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesUpload, actesRead, actesApply, actesHistory, actesUploadFile,
  actesDossierActes, actesFilePoste, actesPosteFile,
  type ActesAnalysis, type ActesCandidate, type ActesHistoryRow, type ActesReference,
} from '@/integrations/actesApi';
import { DocTypePicker, DossierSuggest, TargetSearch, ResponsiblePicker } from './ReceptionPickers';
import { DocViewer } from './DocViewer';
import { sendToPoste } from './poste';
import { countryOfRef, actesEn } from '@/lib/actesCountry';

/**
 * RÉCEPTION DES ACTES (IA) — the screen a scanned act actually arrives through.
 *
 * The flow is the French team's, step for step, because every step exists for
 * a reason somebody paid for once: documents are STAGED before being read (so
 * a type hint can be given), read in a batch, and then reviewed one card at a
 * time — and while a card is waiting for a decision nothing new may be dropped,
 * because two half-filed acts is how one ends up on the wrong dossier.
 *
 * Approving never overwrites. A request that already carries a document gets
 * the new one ADDED to it; the dates stay as they were unless someone says
 * otherwise. A document whose pages look unrelated cannot be approved until a
 * person ticks that they checked. An AR or a courrier is not an act at all and
 * defaults to the dossier's Poste log instead.
 */
type ItemStatus = 'analyzing' | 'review' | 'applied' | 'error';

interface Item {
  key: number;
  fileName: string;
  path: string;
  mime: string;
  status: ItemStatus;
  analysis?: ActesAnalysis;
  match?: ActesCandidate | null;
  candidates?: ActesCandidate[];
  dossierId: string;
  dossierName: string;
  acteId: string;
  statusCode: string;
  acteOptions: { acte_request_id: string; person: string; type: string | null }[];
  emissionDate: string;
  overrideType: string;
  isForeign: boolean;
  createMode: boolean;
  newPerson: string;
  sansDossier: boolean;
  driveUrl: string;
  assignedIds: string[];
  conflict?: string;
  targetHasDoc?: boolean;
  targetLabel?: string;
  targetDossierName?: string;
  targetDossierHubspotId?: string | null;
  mismatchConfirmed?: boolean;
  replaceDates?: boolean;
  duplicate?: { id: string; label: string };
  /* Repliée : la ligne reste, on ne la voit plus en entier. Un lot de dix
     scans est une page de dix mètres si tout est déplié. */
  folded?: boolean;
  /* Le code de série lu SUR l'image, quand c'est une e-photo. */
  ephotoCode?: string;
  posteMode?: boolean;
  posteDossierId?: string;
  posteDossierName?: string;
  fileUrl?: string | null;
  error?: string;
  /* ── WHERE THE READING IS (6 Oct 2026: «no entiendes qué pasa, cuánto
     tarda, por qué documento va») ── When the read started, whether it is
     still waiting its turn, and its place in the batch. */
  startedAt?: number;
  queued?: boolean;
  batchN?: number;
  batchTotal?: number;
  /* Leaving the list: the card folds away before it is removed. */
  leaving?: boolean;
}

/* A read that has not answered in this long is not coming: the server's own
   limit is 120 s. Past it the line says so and offers to read again, instead
   of «Reading…» forever. */
const READ_TIMEOUT_MS = 130_000;
/* Two at a time: ten scans fired at once queued behind each other at the
   model and every one of them looked stuck. */
const READ_PARALLEL = 2;

interface Staged { key: number; file: File; fileName: string; path: string; mime: string; typeHint: string; uploading: boolean }

/* An AR, a recorded letter, a notification: mail, not an act of civil status.
   It belongs in the dossier's Poste log, so the destination defaults there. */
const POSTE_HINTS = [
  'avis de reception', 'accuse de reception', 'lettre recommandee', 'recommande',
  'courrier', 'la poste', 'recepisse', 'pli', 'notification',
  /* L'e-photo : une photo d'identité avec son code de série. Elle n'est pas un
     acte d'état civil — elle se range avec le courrier, sur le dossier de la
     personne. */
  'e-photo', 'ephoto', 'photo d identite', 'photo didentite', 'photo d’identité',
];
const looksLikePoste = (...parts: (string | null | undefined)[]): boolean => {
  const hay = parts.filter(Boolean).join(' ').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return !!hay.trim() && POSTE_HINTS.some((h) => hay.includes(h));
};
const toDateInput = (s: string | null | undefined) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '');

export function ReceptionPanel({ lang, reference }: { lang: Lang; reference: ActesReference }) {
  const L: 'fr' | 'en' = lang === 'fr' ? 'fr' : 'en';
  const T = (fr: string, en: string) => (L === 'fr' ? fr : actesEn(en));
  /* PORTUGAL: the same intake, read in English for Portuguese nationality
     documents; every document is filed into a request of a dossier — no
     Poste log, no «attach later». */
  const pt = countryOfRef(reference) === 'PT';
  const qc = useQueryClient();

  const [tab, setTab] = useState<'intake' | 'history' | 'manual'>('intake');
  const [items, setItems] = useState<Item[]>([]);
  const [staged, setStaged] = useState<Staged[]>([]);
  const [launching, setLaunching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  /* Ce panneau n'avait aucun endroit pour DIRE qu'une chose a échoué : les
     deux échecs qui comptent — un fichier qui ne monte pas, un lien qui ne
     s'ouvre pas — étaient avalés en silence. */
  const [err, setErr] = useState('');
  const [history, setHistory] = useState<ActesHistoryRow[]>([]);
  const [performedBy, setPerformedBy] = useState(reference.me.id);
  const keyRef = useRef(0);
  const pendingRef = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  const update = (key: number, patch: Partial<Item>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  /* WHO FILED IT. It defaults to whoever is signed in — the portal knows the
     e-mail, Actes matches it to a profile, and that profile is `me`. The list
     was the four super admins, which meant everybody else's name was simply
     not there; a deposit is made by whoever opened the envelope, and that is
     usually not one of the four. Everyone active, you first. */
  const admins = [
    ...reference.profiles.filter((p) => p.id === reference.me.id),
    ...reference.profiles.filter((p) => p.id !== reference.me.id),
  ];
  const typeLabel = (det: string | null): string => {
    const d = (det ?? '').toLowerCase().trim();
    if (!d) return '';
    const hit = reference.acteTypes.find((t) => t.label_fr.toLowerCase() === d)
      ?? reference.acteTypes.find((t) => t.label_fr.toLowerCase().includes(d) || d.includes(t.label_fr.toLowerCase()));
    return hit?.label_fr ?? '';
  };

  /* Staged, not read: the file goes to storage now so the browser is not
     holding it, and waits for its type hint and the batch launch. */
  const stage = useCallback(async (file: File) => {
    if (pendingRef.current) return;
    setItems((prev) => prev.filter((i) => i.status !== 'applied'));
    const key = ++keyRef.current;
    setStaged((prev) => [...prev, { key, file, fileName: file.name, path: '', mime: file.type, typeHint: '', uploading: true }]);
    try {
      const up = await actesUpload(file, '_intake');
      setStaged((prev) => prev.map((s) => (s.key === key ? { ...s, path: up.path, uploading: false } : s)));
    } catch (e) {
      /* Un fichier qui n'a pas pu monter DISPARAISSAIT de la liste : rien ne
         distinguait « pas déposé » de « jamais glissé », et le scan n'était
         jamais classé. On le retire, mais on le dit. */
      setStaged((prev) => prev.filter((s) => s.key !== key));
      setErr(`${file.name} — ${friendlyError(e, lang)}`);
    }
  }, []);

  /* ── LIRE, ET POUVOIR RELIRE ──
     La lecture vivait dans `analyse`, qui crée toujours une ligne neuve : pour
     redemander au modèle il fallait re-téléverser le fichier. Elle est
     maintenant à part et prend une CLÉ : la même ligne peut donc être relue,
     autant de fois qu'il faut, sans rien perdre de ce qui a déjà été choisi
     à la main. */
  async function readInto(key: number, src: { path: string; mime: string; typeHint: string }) {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), READ_TIMEOUT_MS);
    update(key, { queued: false, startedAt: Date.now() });
    try {
      const r = await actesRead({ path: src.path, mime: src.mime, typeHint: src.typeHint }, ctrl.signal);
      const a = r.analysis;
      const m = r.match;
      /* ── UN ACCUSÉ DE RÉCEPTION VA À LA POSTE, MAIS PAS DANS LE VIDE ──
         La règle disait : « ça ressemble à du courrier ET on n'a trouvé
         personne → Poste ». Donc dès que le nom de la personne tombait juste,
         l'AR partait se ranger dans une DEMANDE, alors que sa place est le
         suivi de la Poste ; et quand il n'y avait pas de correspondance, il
         allait bien à la Poste mais sans dossier, à choisir à la main.
         Les deux moitiés se corrigent l'une l'autre : la ressemblance décide
         de la DESTINATION, la correspondance décide du DOSSIER. */
      /* Portugal has no Poste log: everything is filed into a request. */
      const poste = !pt && looksLikePoste(src.typeHint, a.detected_type, a.detected_authority);
      update(key, {
        status: 'review', analysis: a, match: m, candidates: r.candidates ?? [],
        dossierId: m?.dossier_id ?? '', acteId: m?.acte_request_id ?? '',
        targetHasDoc: m?.has_document ?? false,
        targetLabel: m?.person_concerned ?? '',
        targetDossierName: m?.dossier_name ?? '',
        targetDossierHubspotId: m?.dossier_hubspot_id ?? null,
        posteMode: poste,
        /* Le dossier de la correspondance sert de destination au courrier :
           c'est le même dossier, trouvé par le même nom. */
        posteDossierId: poste ? (m?.dossier_id ?? '') : '',
        posteDossierName: poste ? (m?.dossier_name ?? '') : '',
        statusCode: a.new_status_code ?? 'recu_physiquement',
        emissionDate: toDateInput(a.detected_date),
        /* Portugal keeps the document as read, in words (it becomes the request line). */
        overrideType: src.typeHint || (pt ? a.detected_type ?? '' : typeLabel(a.detected_type)),
        isForeign: a.is_foreign ?? false,
        ephotoCode: a.ephoto_code ?? '',
        error: undefined,
      });
    } catch (e) {
      update(key, {
        status: 'error',
        error: ctrl.signal.aborted
          ? T('La lecture n’a pas répondu à temps. Relancez-la avec ↻ — le fichier est bien enregistré.',
            'The reading did not answer in time. Run it again with ↻ — the file is safely stored.')
          : friendlyError(e, lang),
      });
    } finally {
      window.clearTimeout(timer);
    }
  }

  /** Redemander au modèle, sur la ligne qui est déjà là. */
  async function reread(item: Item) {
    /* The old reading goes: the line shows the progress again, not stale facts. */
    update(item.key, { status: 'analyzing', error: undefined, folded: false, analysis: undefined });
    await readInto(item.key, { path: item.path, mime: item.mime, typeHint: item.overrideType || '' });
  }

  async function launch() {
    const ready = staged.filter((s) => s.path && !s.uploading);
    if (!ready.length) return;
    setLaunching(true);
    setStaged([]);
    /* Every document gets its line at once — in the order it was dropped,
       numbered — and waits its turn: two are read at a time. */
    const jobs = ready.map((st, i) => ({ key: ++keyRef.current, st, n: i + 1 }));
    setItems((prev) => [...jobs.map(({ key, st, n }): Item => ({
      key, fileName: st.fileName, path: st.path, mime: st.mime, status: 'analyzing', queued: true,
      batchN: n, batchTotal: jobs.length,
      dossierId: '', dossierName: '', acteId: '', statusCode: 'recu_physiquement', acteOptions: [],
      emissionDate: '', overrideType: st.typeHint, isForeign: false,
      createMode: false, newPerson: '', sansDossier: false, driveUrl: '', assignedIds: [],
    })), ...prev]);
    let next = 0;
    const worker = async () => {
      while (next < jobs.length) {
        const { key, st } = jobs[next++];
        await readInto(key, { path: st.path, mime: st.mime, typeHint: st.typeHint });
      }
    };
    await Promise.all(Array.from({ length: Math.min(READ_PARALLEL, jobs.length) }, worker));
    setLaunching(false);
  }

  /* Rejecting folds the card away first — then it leaves the list. */
  const removeItem = (key: number) => {
    update(key, { leaving: true });
    window.setTimeout(() => setItems((prev) => prev.filter((x) => x.key !== key)), 260);
  };

  async function pickDossier(item: Item, id: string, name: string) {
    update(item.key, { dossierId: id, dossierName: name, acteId: '', acteOptions: [], sansDossier: false });
    if (!id) return;
    try { update(item.key, { acteOptions: await actesDossierActes(id) }); } catch { /* the list is a courtesy */ }
  }

  async function approve(item: Item, onConflict?: 'attach', forceCreate?: boolean) {
    if (item.posteMode) {
      if (!item.posteDossierId) return;
      update(item.key, { status: 'analyzing' });
      try {
        await actesFilePoste(item.posteDossierId, { file_path: item.path, file_name: item.fileName, file_mime: item.mime });
        update(item.key, { status: 'applied', error: undefined });
        await qc.invalidateQueries({ queryKey: ['actes'] });
      } catch (e) { update(item.key, { status: 'review', error: friendlyError(e, lang) }); }
      return;
    }
    const creating = !item.acteId && item.createMode;
    if (!item.acteId && !(creating && item.newPerson.trim() && (item.dossierId || item.sansDossier))) return;

    update(item.key, { status: 'analyzing', conflict: undefined, duplicate: undefined });
    const body: Record<string, unknown> = {
      path: item.path, name: item.fileName, mime: item.mime,
      status_code: item.statusCode, performed_by: performedBy,
      is_foreign: item.isForeign,
      ephoto_code: item.ephotoCode || null,
    };
    if (onConflict) body.on_conflict = onConflict;
    if (forceCreate) body.force_create = true;
    /* On a conflict the existing dates stand unless somebody asks otherwise —
       an act filed months ago does not get today's reception date. */
    if (onConflict && item.replaceDates !== true) body.keep_dates = true;
    if (item.emissionDate) body.emission_date = item.emissionDate;
    if (item.overrideType) body.acte_type = item.overrideType;
    if (item.acteId) body.acte_request_id = item.acteId;
    else {
      body.new_dossier_id = item.dossierId || null;
      body.new_person = item.newPerson;
      if (item.overrideType) body.new_acte_type = item.overrideType;
      if (item.driveUrl.trim()) body.new_drive_url = item.driveUrl.trim();
      if (item.assignedIds.length) body.new_assigned_ids = item.assignedIds;
    }
    try {
      const r = await actesApply(body);
      if (r.conflict) { update(item.key, { status: 'review', conflict: r.existing_file_name ?? 'document' }); return; }
      if (r.duplicate) { update(item.key, { status: 'review', duplicate: { id: r.existing_acte_id ?? '', label: r.existing_label ?? '' } }); return; }
      update(item.key, { status: 'applied', fileUrl: r.file_url ?? null, error: undefined });
      await qc.invalidateQueries({ queryKey: ['actes'] });
      void loadHistory();
    } catch (e) {
      update(item.key, { status: 'review', error: friendlyError(e, lang) });
    }
  }

  const loadHistory = useCallback(async () => {
    try { setHistory(await actesHistory()); } catch { /* the tab shows empty */ }
  }, []);
  useEffect(() => { if (tab === 'history') void loadHistory(); }, [tab, loadHistory]);

  /* A document is pending until validated or rejected, and while one is
     pending nothing else may be dropped. */
  const pending = items.some((i) => !i.leaving && (i.status === 'review' || i.status === 'analyzing'));
  const reading = items.filter((i) => !i.leaving && i.status === 'analyzing' && !i.analysis);
  const toReview = items.filter((i) => !i.leaving && i.status === 'review').length;
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  return (
    <section className="actes-panel">
      {/* Ce qui a échoué se dit ici, une fois, et se referme d'un clic. */}
      {err && (
        <p className="muted-note ni-err" onClick={() => setErr('')} role="status">⚠ {err}</p>
      )}
      <div className="actes-tabline">
        <div className="res-chips" role="tablist">
          <button className={`res-chip ${tab === 'intake' ? 'on' : ''}`} onClick={() => setTab('intake')}>
            <KBIcon name="ai" size={13} /> {T('Réception', 'Intake')}
          </button>
          <button className={`res-chip ${tab === 'history' ? 'on' : ''}`} onClick={() => setTab('history')}>
            <KBIcon name="clock" size={13} /> {T('Historique', 'History')}
          </button>
          {/* France's manual describes the Poste and the French acts; Portugal's lives in its Guide. */}
          {!pt && (
          <button className={`res-chip ${tab === 'manual' ? 'on' : ''}`} onClick={() => setTab('manual')}>
            <KBIcon name="logbook" size={13} /> {T('Manuel', 'Manual')}
          </button>
          )}
        </div>
        {/* Who is doing the intake. It is stamped on the reception, so it is
            asked here rather than assumed. */}
        {/* Standing in for a colleague is a super admin's act — the person who
            opened the envelope is at the scanner, not at the screen. For
            everybody else the deposit is simply theirs, and a select that
            cannot be honoured must not be offered. */}
        <label className="actes-field inline" hidden={!reference.me.isSuper}>
          <span>{T('Réalisé par', 'Performed by')}</span>
          <select value={performedBy} onChange={(e) => setPerformedBy(e.target.value)}>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {(a.full_name ?? a.email) + (a.id === reference.me.id ? T(' (vous)', ' (you)') : '')}
              </option>
            ))}
          </select>
        </label>
      </div>

      {tab === 'intake' ? (
        <>
          <p className="muted-note">
            {T('Un document par acte. Déposez les scans, donnez un indice de type si vous voulez, puis lancez l’identification.',
              'One document per act. Drop the scans, give a type hint if you want, then launch the identification.')}
          </p>

          <div
            className={`actes-drop ${dragOver ? 'over' : ''} ${pending ? 'off' : ''}`}
            onDragOver={(e) => { if (!pending) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!pending) Array.from(e.dataTransfer.files ?? []).forEach((f) => void stage(f)); }}
            onClick={() => { if (!pending) input.current?.click(); }}
          >
            <KBIcon name="documents" size={22} />
            <strong>{reading.length > 0
              ? T(`Lecture en cours — ${reading.length} document${reading.length > 1 ? 's' : ''}`, `Reading — ${reading.length} document${reading.length > 1 ? 's' : ''}`)
              : pending ? T('Validez ou rejetez les documents lus', 'Validate or reject the documents read') : T('Déposez les documents ici', 'Drop the documents here')}</strong>
            <span>{reading.length > 0
              ? (toReview > 0
                ? T('Vous pouvez déjà vérifier ceux qui sont prêts, plus bas.', 'You can already check the ready ones, below.')
                : T('Environ 30 secondes par document. Restez sur cet écran : la lecture continue tant qu’il est ouvert.', 'About 30 seconds per document. Stay on this screen: the reading goes on while it is open.'))
              : pending
                ? T('Ensuite, vous pourrez en déposer d’autres.', 'Then you can drop more.')
                : T('PDF, PNG, JPEG ou WEBP — 15 Mo maximum', 'PDF, PNG, JPEG or WEBP — 15 MB maximum')}</span>
            <input ref={input} type="file" multiple hidden disabled={pending}
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => { Array.from(e.target.files ?? []).forEach((f) => void stage(f)); e.target.value = ''; }} />
          </div>

          {staged.length > 0 && (
            <div className="actes-staged">
              <div className="actes-staged-head">
                <KBIcon name="documents" size={14} />
                {T('Documents à identifier', 'Documents to identify')}
                <span className="n">{staged.length}</span>
              </div>
              {staged.map((s, i) => (
                <div key={s.key} className="actes-staged-row">
                  <span className="i">{i + 1}.</span>
                  <span className="nm">{s.fileName}</span>
                  {pt ? (
                    /* Portugal: a hint in words, or nothing — the reading detects it. */
                    <input className="actes-picker sm" value={s.typeHint} placeholder="Auto-detect"
                      onChange={(e) => setStaged((prev) => prev.map((x) => (x.key === s.key ? { ...x, typeHint: e.target.value } : x)))} />
                  ) : (
                  <DocTypePicker compact lang={L} types={reference.acteTypes} value={s.typeHint}
                    onChange={(label) => setStaged((prev) => prev.map((x) => (x.key === s.key ? { ...x, typeHint: label } : x)))} />
                  )}
                  <button className="actes-row-act" onClick={() => setStaged((prev) => prev.filter((x) => x.key !== s.key))}>×</button>
                </div>
              ))}
              <button className="btn" disabled={launching || staged.some((s) => s.uploading)} onClick={() => void launch()}>
                {launching ? '…' : <><KBIcon name="ai" size={14} /> {T('Lancer l’identification', 'Launch the identification')}</>}
              </button>
            </div>
          )}

          {items.map((it) => (
            <IntakeCard
              key={it.key}
              item={it}
              lang={L}
              reference={reference}
              onUpdate={(patch) => update(it.key, patch)}
              onPickDossier={(id, name) => void pickDossier(it, id, name)}
              onApprove={(oc, fc) => void approve(it, oc, fc)}
              onReject={() => removeItem(it.key)}
              onReread={() => void reread(it)}
            />
          ))}
        </>
      ) : tab === 'history' ? (
        <HistoryList rows={history} lang={L} />
      ) : (
        <ManualTab lang={L} />
      )}
    </section>
  );
}

/* ── One document, under review ─────────────────────────────────────────── */
function IntakeCard({ item, lang, reference, onUpdate, onPickDossier, onApprove, onReject, onReread }: {
  item: Item; lang: 'fr' | 'en'; reference: ActesReference;
  onUpdate: (patch: Partial<Item>) => void;
  onPickDossier: (id: string, name: string) => void;
  onApprove: (onConflict?: 'attach', forceCreate?: boolean) => void;
  onReject: () => void;
  onReread: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const pt = countryOfRef(reference) === 'PT';
  const [visN, setVisN] = useState(2);
  const a = item.analysis;

  /* Pages that look unrelated: a mixed scan files an act on a stranger's
     dossier. It cannot be approved until somebody says they checked. */
  const needsConfirm = !!a?.multi_document && !item.mismatchConfirmed;
  const canApprove = !needsConfirm && (item.posteMode
    ? !!item.posteDossierId
    : !!item.acteId || (item.createMode && !!item.newPerson.trim() && (!!item.dossierId || item.sansDossier)));

  const startCreate = () => onUpdate({
    createMode: true, acteId: '', sansDossier: false, newPerson: '',
    overrideType: item.overrideType || '',
  });

  return (
    <div className={`actes-card2 ${item.status} ${item.leaving ? 'leaving' : ''}`}>
      <div className="actes-card2-head">
        {/* ── TROIS GESTES QUI MANQUAIENT SUR CHAQUE LIGNE ──
            Replier (un lot de dix scans dépliés est une page de dix mètres),
            RELIRE (le modèle s'est trompé, ou il a échoué : il fallait
            re-téléverser le fichier pour lui redemander), et RETIRER la ligne
            (elle n'avait de sortie que « Rejeter », qui n'existe pas tant que
            la lecture n'a pas abouti). */}
        <button type="button" className="actes-row-act" aria-expanded={!item.folded}
          title={item.folded ? T('Déplier', 'Unfold') : T('Replier', 'Fold')}
          onClick={() => onUpdate({ folded: !item.folded })}>
          <KBIcon name="chevron" size={13} />
        </button>
        <KBIcon name="documents" size={14} />
        <strong>{item.fileName}</strong>
        {(item.batchTotal ?? 0) > 1 && <span className="actes-batchn">{item.batchN}/{item.batchTotal}</span>}
        <span className={`actes-tag ${item.status === 'applied' ? 'c-emerald' : item.status === 'error' ? 'c-rose' : 'c-amber'} ${item.status === 'analyzing' ? 'live' : ''}`}>
          {item.status === 'applied' ? T('Validé', 'Applied')
            : item.status === 'analyzing' ? (item.queued ? T('En attente', 'Queued') : a ? T('Enregistrement…', 'Saving…') : T('Lecture…', 'Reading…'))
              : item.status === 'error' ? T('Erreur', 'Error') : T('À vérifier', 'Review')}
        </span>
        <span className="actes-card2-acts">
          {item.status !== 'applied' && (
            <button type="button" className="actes-row-act" disabled={item.status === 'analyzing'}
              title={T('Relire le document', 'Read the document again')} onClick={onReread}>
              <KBIcon name="reset" size={13} />
            </button>
          )}
          <button type="button" className="actes-row-act danger" title={T('Retirer de la liste', 'Remove from the list')}
            onClick={onReject}>
            <KBIcon name="x" size={13} />
          </button>
        </span>
      </div>

      {item.error && <p className="muted-note ni-err">⚠ {item.error}</p>}

      {item.status === 'analyzing' && !a ? (
        /* Folded or not, a line being read says how far it has got. */
        <ReadingProgress lang={lang} startedAt={item.startedAt} queued={!!item.queued} />
      ) : item.folded ? (
        <p className="muted-note">
          {[a?.detected_person, a?.detected_type].filter(Boolean).join(' · ') || item.fileName}
        </p>
      ) : a && item.status !== 'error' && (
        <div className="actes-cbody">
          {/* ── LO LEÍDO, EN UNA LÍNEA (6 Oct 2026: «súper grande la card… más
              conciso») ── Siete casillas con su título en mayúsculas eran media
              pantalla; ahora es una frase: quién, qué, de dónde, cuándo. */}
          <div className="actes-facts">
            <strong>{a.detected_person || T('Personne non lue', 'Person not read')}</strong>
            {[a.detected_type, a.detected_authority, a.detected_date, a.detected_reference && `réf. ${a.detected_reference}`,
              a.page_count != null && `${a.page_count} p.`].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
            <em className={`actes-conf ${(a.confidence ?? 0) >= 0.8 ? 'hi' : (a.confidence ?? 0) >= 0.5 ? 'mid' : 'lo'}`}
              title={T('Confiance de la correspondance', 'Match confidence')}>
              {Math.round((a.confidence ?? 0) * 100)}%
            </em>
          </div>

          {item.status === 'applied' ? null : (
            <>
          {a.multi_document && (
            <div className="actes-warn compact">
              <strong>⚠ {T('Ce PDF contient peut-être deux documents', 'This PDF may hold two documents')}</strong>
              {a.multi_document_reason && <p><em>{a.multi_document_reason}</em></p>}
              {/* Le pourquoi reste là, replié : on le lit une fois, pas à
                  chaque document. */}
              <details className="actes-why">
                <summary>{T('Pourquoi c’est bloquant', 'Why this blocks')}</summary>
                <p>{T('Le classement se fait sur UNE demande : si le PDF en mélange deux, l’acte de quelqu’un d’autre part sur ce dossier. « Valider » attend la case ; tout le reste reste possible.',
                  'Filing happens on ONE request: if the PDF mixes two, somebody else’s act lands on this dossier. “Approve” waits for the box; everything else stays possible.')}</p>
              </details>
              <label className="actes-check">
                <input type="checkbox" checked={!!item.mismatchConfirmed}
                  onChange={(e) => onUpdate({ mismatchConfirmed: e.target.checked })} />
                {T('J’ai vérifié, les pages vont bien ensemble', 'I checked — the pages do belong together')}
              </label>
            </div>
          )}

              <div className="actes-row3">
                <label className="actes-field">
                  <span>{T('Date d’émission', 'Issue date')}</span>
                  <input type="date" value={item.emissionDate} onChange={(e) => onUpdate({ emissionDate: e.target.value })} />
                </label>
                <div className="actes-field">
                  <span>{pt ? 'Document' : T('Type', 'Type')}</span>
                  {pt ? (
                    <input value={item.overrideType} placeholder="e.g. Birth certificate"
                      onChange={(e) => onUpdate({ overrideType: e.target.value })} />
                  ) : (
                  <DocTypePicker lang={lang} types={reference.acteTypes} value={item.overrideType}
                    onChange={(label) => onUpdate({ overrideType: label })} />
                  )}
                </div>
                <div className="actes-field">
                  <span>{T('Origine', 'Origin')}</span>
                  {pt ? (
                    <div className="actes-seg" role="group"
                      title="Issued by a Portuguese authority (Conservatória, IRN, a Portuguese consulate) or abroad.">
                      <button type="button" className={!item.isForeign ? 'on' : ''} onClick={() => onUpdate({ isForeign: false })}>Portuguese</button>
                      <button type="button" className={item.isForeign ? 'on' : ''} onClick={() => onUpdate({ isForeign: true })}>Foreign</button>
                    </div>
                  ) : (
                  <div className="actes-seg" role="group"
                    title={T('Une transcription du SCEC ou d’un consulat reste un acte français.', 'A transcription by the SCEC or a consulate is still a French act.')}>
                    <button type="button" className={!item.isForeign ? 'on' : ''} onClick={() => onUpdate({ isForeign: false })}>{T('Français', 'French')}</button>
                    <button type="button" className={item.isForeign ? 'on' : ''} onClick={() => onUpdate({ isForeign: true })}>{T('Étranger', 'Foreign')}</button>
                  </div>
                  )}
                </div>
              </div>

              {/* Where does it go: a request, or the dossier's Poste log
                  (France only — Portugal files every document into a request). */}
              <div className="actes-fieldset">
                <span className="cap">{T('Destination', 'Destination')}</span>
                {!pt && <div className="res-chips">
                  <button className={`res-chip ${!item.posteMode ? 'on' : ''}`} onClick={() => onUpdate({ posteMode: false })}>
                    <KBIcon name="documents" size={12} /> {T('Demande d’acte', 'Civil-record request')}
                  </button>
                  <button className={`res-chip ${item.posteMode ? 'on' : ''}`} onClick={() => onUpdate(sendToPoste(item))}>
                    <KBIcon name="mail" size={12} /> {T('Suivi de la Poste', 'Poste log')}
                  </button>
                </div>}

                {item.posteMode ? (
                  <div className="actes-sub">
                    <p className="muted-note">{T('Un courrier est toujours rattaché à un dossier.', 'Mail is always tied to a dossier.')}</p>
                    <DossierSuggest lang={lang} initialQuery={a.detected_person ?? ''}
                      selectedId={item.posteDossierId ?? ''} selectedName={item.posteDossierName}
                      onPick={(id, name) => onUpdate({ posteDossierId: id, posteDossierName: name })} />
                    {!item.posteDossierId && <p className="muted-note">⚠ {T('Choisissez le dossier.', 'Choose the dossier.')}</p>}
                  </div>
                ) : item.acteId && !item.createMode ? (
                  <div className="actes-chosen">
                    <KBIcon name="check" size={13} />
                    <span className="actes-list-main">
                      <strong>{item.targetLabel || '—'}</strong>
                      <em>
                        {item.targetDossierName || T('sans dossier', 'no dossier')}
                        {item.targetDossierHubspotId ? ` · ID ${item.targetDossierHubspotId}` : ''}
                      </em>
                    </span>
                    <button onClick={() => onUpdate({ acteId: '', targetHasDoc: false, targetLabel: '', targetDossierName: '', targetDossierHubspotId: null })}>
                      {T('changer', 'change')}
                    </button>
                  </div>
                ) : item.createMode ? (
                  <div className="actes-sub">
                    <button className="actes-back" onClick={() => onUpdate({ createMode: false, newPerson: '', sansDossier: false })}>
                      <KBIcon name="back" size={13} /> {T('Revenir aux correspondances', 'Back to the matches')}
                    </button>
                    <p className="muted-note">{T('Nouvelle demande d’acte', 'New civil-record request')}</p>
                    {!item.sansDossier && (
                      <>
                        <DossierSuggest lang={lang} selectedId={item.dossierId} selectedName={item.dossierName}
                          onPick={(id, name) => onPickDossier(id, name)} />
                        {item.dossierId && item.acteOptions.length > 0 && (
                          <details className="ni-list">
                            <summary>{T(`Personnes déjà dans ce dossier (${item.acteOptions.length})`, `People already in this dossier (${item.acteOptions.length})`)}</summary>
                            <ul>
                              {item.acteOptions.map((o) => (
                                <li key={o.acte_request_id}>
                                  <span className="ni-li-t">{o.person}</span>
                                  <span className="ni-li-m">{o.type ?? ''}</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </>
                    )}
                    <label className="actes-field">
                      <span>{T('Personne concernée', 'Person concerned')}</span>
                      <input value={item.newPerson} onChange={(e) => onUpdate({ newPerson: e.target.value })}
                        placeholder={T('Prénom NOM', 'First LAST')} />
                    </label>
                    {item.sansDossier && (
                      <>
                        <label className="actes-field">
                          <span>{T('Lien Drive', 'Drive link')}</span>
                          <input value={item.driveUrl} onChange={(e) => onUpdate({ driveUrl: e.target.value })} placeholder="https://drive.google.com/…" />
                        </label>
                        <div className="actes-field">
                          <span>{T('Responsables', 'Responsibles')}</span>
                          <ResponsiblePicker lang={lang} people={reference.profiles} selected={item.assignedIds}
                            onChange={(ids) => onUpdate({ assignedIds: ids })} />
                          <p className="muted-note">
                            {T('Sans responsable, tout le monde est prévenu pour que la bonne personne le reconnaisse.',
                              'With nobody responsible, everyone is told so the right person recognises it.')}
                          </p>
                        </div>
                      </>
                    )}
                    {/* ── LE CODE DE L'E-PHOTO, AVANT DE VALIDER ──
                        Il vient de l'image et c'est lui qui relie la photo à
                        la personne : il se relit et se corrige ICI, pas après
                        coup dans la fiche. Vide s'il n'y en a pas — on ne
                        devine pas un code. */}
                    {(item.ephotoCode || a?.is_ephoto) && (
                      <label className="actes-field">
                        <span>{T('Code e-photo', 'e-Photo code')}</span>
                        <input value={item.ephotoCode ?? ''} onChange={(e) => onUpdate({ ephotoCode: e.target.value })}
                          placeholder={T('imprimé sur la photo', 'printed on the photo')} />
                      </label>
                    )}
                    {!pt && (
                    <label className="actes-check">
                      <input type="checkbox" checked={item.sansDossier}
                        onChange={(e) => onUpdate({ sansDossier: e.target.checked, dossierId: e.target.checked ? '' : item.dossierId })} />
                      {T('Sans dossier (à rattacher plus tard)', 'No dossier (attach later)')}
                    </label>
                    )}
                  </div>
                ) : (
                  <div className="actes-sub">
                    {!item.match && <p className="muted-note">{T('Aucune correspondance certaine.', 'No certain match.')}</p>}
                    {(item.candidates?.length ?? 0) > 0 && (
                      <>
                        <span className="cap">{T('Correspondances possibles', 'Possible matches')}</span>
                        {item.candidates!.slice(0, visN).map((c) => {
                          const label = c.person_concerned || c.request_line || '—';
                          return (
                            <button key={c.acte_request_id} className="actes-suggest-row"
                              onClick={() => onUpdate({
                                acteId: c.acte_request_id, dossierId: c.dossier_id ?? '', targetHasDoc: c.has_document,
                                targetLabel: label, targetDossierName: c.dossier_name,
                                targetDossierHubspotId: c.dossier_hubspot_id ?? null,
                              })}>
                              <KBIcon name="user" size={13} />
                              <span className="actes-list-main">
                                <strong>{label}</strong>
                                <em>
                                  {c.dossier_name || T('sans dossier', 'no dossier')}
                                  {c.dossier_hubspot_id ? ` · ID ${c.dossier_hubspot_id}` : ''}
                                  {c.acte_type ? ` · ${c.acte_type}` : ''}
                                </em>
                              </span>
                              {c.has_document && <KBIcon name="documents" size={12} />}
                              <KBIcon name="chevron-right" size={13} />
                            </button>
                          );
                        })}
                        {(item.candidates?.length ?? 0) > visN && (
                          <button className="btn btn-sm btn-ghost" onClick={() => setVisN((n) => n + 2)}>
                            {T('Voir plus', 'Show more')}
                          </button>
                        )}
                      </>
                    )}
                    <details className="ni-list" open={!(item.candidates?.length)}>
                      <summary>{T('Chercher dans toute la base', 'Search the whole base')}</summary>
                      <TargetSearch lang={lang}
                        onPick={(acteId, dossierId, hasDoc, person, dossierName) => onUpdate({
                          acteId, dossierId: dossierId ?? '', targetHasDoc: hasDoc,
                          targetLabel: person, targetDossierName: dossierName,
                        })} />
                    </details>
                    <button className="btn btn-sm btn-ghost" onClick={startCreate}>
                      <KBIcon name="plus" size={13} /> {T('Créer la demande', 'Create the request')}
                    </button>
                  </div>
                )}
              </div>

              {item.acteId && item.targetHasDoc && !item.conflict && (
                <p className="muted-note">
                  ⚠ {T('Cette demande porte déjà un document. Valider AJOUTE celui-ci, il ne remplace rien — vérifiez que ce n’est pas le même fichier.',
                    'This request already carries a document. Approving ADDS this one, it replaces nothing — check it is not the same file.')}
                </p>
              )}

              {item.conflict && (
                <div className="actes-warn">
                  <strong>⚠ {T(`Un document est déjà attaché : ${item.conflict}`, `A document is already attached: ${item.conflict}`)}</strong>
                  <p>{T('Le nouveau sera ajouté à la liste des documents reçus.', 'The new one will be added to the received list.')}</p>
                  <label className="actes-check">
                    <input type="checkbox" checked={item.replaceDates === true} onChange={(e) => onUpdate({ replaceDates: e.target.checked })} />
                    {T('Mettre aussi les dates à jour', 'Update the dates as well')}
                  </label>
                </div>
              )}

              {item.duplicate && (
                <div className="actes-warn">
                  <strong>⚠ {T(`Une demande identique existe déjà : ${item.duplicate.label}`, `An identical request already exists: ${item.duplicate.label}`)}</strong>
                  <div className="nt-actions">
                    <button className="btn btn-sm btn-ghost"
                      onClick={() => onUpdate({ acteId: item.duplicate!.id, createMode: false, duplicate: undefined })}>
                      {T('Utiliser celle-ci', 'Use that one')}
                    </button>
                    <button className="btn btn-sm" onClick={() => onApprove(undefined, true)}>
                      {T('Créer quand même', 'Create anyway')}
                    </button>
                  </div>
                </div>
              )}

              <div className="actes-card2-foot">
                <label className="actes-field inline">
                  <span>{T('Nouveau statut', 'New status')}</span>
                  <select value={item.statusCode} onChange={(e) => onUpdate({ statusCode: e.target.value })}>
                    {reference.acteStatuses.map((s) => (
                      <option key={s.id} value={s.code}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                    ))}
                  </select>
                </label>
                <div className="nt-actions">
                  <button className="btn btn-sm btn-ghost" onClick={onReject}>{T('Rejeter', 'Reject')}</button>
                  <button className="btn btn-sm" disabled={!canApprove || item.status === 'analyzing'}
                    title={needsConfirm
                      ? T('Cochez d’abord que vous avez vérifié les pages.', 'Tick that you checked the pages first.')
                      : undefined}
                    onClick={() => onApprove(item.conflict ? 'attach' : undefined)}>
                    {item.status === 'analyzing' ? '…' : item.createMode && !item.posteMode ? T('Créer et valider', 'Create and approve') : T('Valider', 'Approve')}
                  </button>
                </div>
              </div>

              {a.reasoning && (
                <details className="ni-list">
                  <summary>{T('Détails de la lecture', 'Reading details')}</summary>
                  <p className="muted-note">{a.reasoning}</p>
                </details>
              )}
            </>
          )}

          {item.status === 'applied' && (
            <div className="actes-applied">
              <KBIcon name="check" size={14} />
              <span>
                {item.posteMode ? T('Classé au suivi de la Poste', 'Filed in the Poste log') : T('Classé', 'Filed')}
                {(item.posteMode ? item.posteDossierName : item.targetDossierName) && <em> · {item.posteMode ? item.posteDossierName : item.targetDossierName}</em>}
              </span>
              {item.fileUrl && (
                <a className="btn btn-sm btn-ghost" href={item.fileUrl} target="_blank" rel="noreferrer">
                  <KBIcon name="eye" size={13} /> {T('Voir', 'View')}
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── LA LECTURE SE VOIT (6 Oct 2026) ──
   «No hace un efecto de que lo está leyendo… cuánto tiempo puede tardar».
   A read takes ~30 s: ~18 to read the scan, ~12 to find its dossier and line
   (measured in production on 6 Oct). The line says which of the two it is
   in, how long it has been, and moves a bar that slows as it nears the end —
   it never claims to be done before the answer is in. */
function ReadingProgress({ lang, startedAt, queued }: { lang: 'fr' | 'en'; startedAt?: number; queued: boolean }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (queued) return;
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(t);
  }, [queued]);
  if (queued) {
    return (
      <div className="actes-reading queued" role="status">
        <span className="actes-reading-txt">{T('En attente — deux documents sont lus à la fois.', 'Waiting — two documents are read at a time.')}</span>
      </div>
    );
  }
  const sec = Math.max(0, Math.round((now - (startedAt || now)) / 1000));
  const phase = sec < 3 ? T('Ouverture du document…', 'Opening the document…')
    : sec < 20 ? T('Lecture du document par l’IA…', 'The AI is reading the document…')
      : sec < 60 ? T('Recherche du dossier et de la ligne…', 'Finding the dossier and the line…')
        : T('Plus long que d’habitude — ça continue…', 'Longer than usual — still going…');
  /* 0 → 85 % over ~35 s, then a slow creep: an honest «almost». */
  const pct = sec <= 35 ? (sec / 35) * 85 : Math.min(97, 85 + (sec - 35) * 0.25);
  return (
    <div className="actes-reading" role="status" aria-live="polite">
      <div className="actes-reading-row">
        <span className="actes-reading-txt">{phase}</span>
        <span className="actes-reading-t">{sec} s · {T('≈ 30 s', '≈ 30 s')}</span>
      </div>
      <div className="actes-reading-bar"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/* ── What has been filed, by whom, when ─────────────────────────────────── */
function HistoryList({ rows, lang }: { rows: ActesHistoryRow[]; lang: 'fr' | 'en' }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [view, setView] = useState<(() => Promise<{ url: string; downloadUrl?: string; name: string | null; mime?: string | null }>) | null>(null);
  const [dossierQ, setDossierQ] = useState('');
  const [uploader, setUploader] = useState('');
  const [day, setDay] = useState('');
  /* Réception or Poste (Gerónimo, 29 Oct 2026: «discriminado como poste, réception»). */
  const [origin, setOrigin] = useState<'' | 'reception' | 'poste'>('');
  const hasPoste = useMemo(() => rows.some((r) => r.source === 'poste'), [rows]);
  const [busy, setBusy] = useState<string | null>(null);
  /* Le téléchargement échouait sans un mot : cette liste n'avait pas non plus
     d'endroit pour le dire. */
  const [dlErr, setDlErr] = useState('');
  /* Days fold. Today is open; the rest of the log is a header you press. */
  const [shut, setShut] = useState<Record<string, boolean>>({});

  const dayKey = (iso: string) => iso.slice(0, 10);
  const uploaders = useMemo(() => [...new Set(rows.map((r) => r.uploaded_by).filter(Boolean))].sort(), [rows]);
  const days = useMemo(() => [...new Set(rows.map((r) => dayKey(r.created_at)))].sort().reverse(), [rows]);
  const shown = useMemo(() => {
    const q = dossierQ.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(r.dossier ?? '').toLowerCase().includes(q)) return false;
      if (uploader && r.uploaded_by !== uploader) return false;
      if (day && dayKey(r.created_at) !== day) return false;
      if (origin && (r.source || 'reception') !== origin) return false;
      return true;
    });
  }, [rows, dossierQ, uploader, day, origin]);

  const byDay = useMemo(() => {
    const m = new Map<string, ActesHistoryRow[]>();
    for (const r of shown) {
      const k = dayKey(r.created_at);
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [shown]);

  const dayLabel = (d: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const yest = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
    if (d === today) return T("Aujourd'hui", 'Today');
    if (d === yest) return T('Hier', 'Yesterday');
    return new Date(`${d}T00:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB',
      { weekday: 'long', day: '2-digit', month: 'long' });
  };

  /* Viewing opens it here; downloading is the one case that still leaves the
     page, because a download IS leaving with the file. */
  /* A Poste line's file lives with its dossier's mail, not with the readings. */
  const fileOf = (h: ActesHistoryRow) => (h.source === 'poste' && h.dossier_id
    ? () => actesPosteFile(h.dossier_id!, h.id)
    : () => actesUploadFile(h.id));
  const open = async (h: ActesHistoryRow, download: boolean) => {
    const id = h.id;
    if (!download) { setView(() => fileOf(h)); return; }
    setBusy(id + 'd');
    try {
      const f = await fileOf(h)();
      /* `window.open` après un `await` est hors du geste de l'utilisateur : le
       navigateur le bloque, et rien ne s'affichait ni ne s'ouvrait. Un <a>
       cliqué programmatiquement passe. */
    if (f.downloadUrl) {
      const a = document.createElement('a');
      a.href = f.downloadUrl; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    }
    } catch (e) { setDlErr(friendlyError(e, lang)); }
    setBusy(null);
  };

  return (
    <>
      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}
      {dlErr && <p className="muted-note ni-err" onClick={() => setDlErr('')} role="status">⚠ {dlErr}</p>}
      <div className="actes-toolbar">
        <div className="actes-filters" style={{ paddingTop: 0, borderTop: 0 }}>
          <label className="actes-find wide">
            <KBIcon name="search" size={14} />
            <input value={dossierQ} onChange={(e) => setDossierQ(e.target.value)} placeholder={T('Filtrer par dossier…', 'Filter by dossier…')} />
          </label>
          <label className="actes-field inline">
            <span>{T('Déposé par', 'Filed by')}</span>
            <select value={uploader} onChange={(e) => setUploader(e.target.value)}>
              <option value="">{T('Tous', 'All')}</option>
              {uploaders.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label className="actes-field inline">
            <span>{T('Jour', 'Day')}</span>
            <select value={day} onChange={(e) => setDay(e.target.value)}>
              <option value="">{T('Tous', 'All')}</option>
              {days.map((d) => <option key={d} value={d}>{new Date(`${d}T00:00:00`).toLocaleDateString()}</option>)}
            </select>
          </label>
          {hasPoste && (
            <label className="actes-field inline">
              <span>{T('Origine', 'Source')}</span>
              <select value={origin} onChange={(e) => setOrigin(e.target.value as '' | 'reception' | 'poste')}>
                <option value="">{T('Tous', 'All')}</option>
                <option value="reception">Réception</option>
                <option value="poste">Poste</option>
              </select>
            </label>
          )}
          <span className="actes-count">{shown.length}</span>
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="muted-note">{T('Rien pour ces filtres.', 'Nothing for these filters.')}</p>
      ) : (
        /* Day by day: a deposit log is read as "what came in today", never as
           one endless list with the date repeated on every line. */
        <>
          {byDay.map(([d, rowsOfDay], i) => {
            /* Today opens; everything older is a header you press. */
            const closed = shut[d] ?? i > 0;
            return (
              <div key={d} className="actes-day">
                <button type="button" className={`actes-day-h ${closed ? 'shut' : ''}`} aria-expanded={!closed}
                  onClick={() => setShut((p) => ({ ...p, [d]: !closed }))}>
                  <KBIcon name="chevron" size={13} />
                  <span className="actes-day-n">{dayLabel(d)}</span>
                  <span className="actes-count">{rowsOfDay.length}</span>
                </button>
                {!closed && (
                  <ul className="actes-up">
                    {rowsOfDay.map((h) => (
                      /* ONE line per upload: the hour, who it is for, what it
                         was read as, the file, and who filed it. Everything
                         that does not fit is trimmed with an ellipsis rather
                         than wrapped onto a third row. */
                      <li key={h.id}>
                        <span className="t">
                          {new Date(h.created_at).toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="n" title={h.dossier ?? ''}>{h.dossier ?? h.file_name ?? '—'}</span>
                        {/* Which side of the house it came through: a reading of
                            the Réception, or mail filed in the dossier's Poste. */}
                        <span className="k" title={h.source === 'poste' ? (h.summary ?? '') : (h.detected_type ?? '')}>
                          <em className={`actes-src ${h.source === 'poste' ? 'poste' : 'rec'}`}>{h.source === 'poste' ? 'Poste' : 'Réception'}</em>
                          {h.source === 'poste' ? (h.summary || T('Courrier', 'Mail')) : (h.detected_type ?? '—')}
                        </span>
                        <span className="f" title={h.file_name ?? ''}>{h.file_name ?? '—'}</span>
                        <span className="u" title={h.uploaded_by ?? ''}>{h.uploaded_by ?? '—'}</span>
                        <span className="a">
                          {h.has_file && (
                            <>
                              <button className="actes-row-act" disabled={busy === h.id + 'v'} title={T('Voir', 'View')}
                                onClick={() => void open(h, false)}><KBIcon name="eye" size={13} /></button>
                              <button className="actes-row-act" disabled={busy === h.id + 'd'} title={T('Télécharger', 'Download')}
                                onClick={() => void open(h, true)}><KBIcon name="download" size={13} /></button>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

/* ── The eight steps, written down where the flow lives ─────────────────── */
function ManualTab({ lang }: { lang: 'fr' | 'en' }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const steps: [string, string][] = [
    [T('1. Déposer', '1. Drop'), T('Glissez un ou plusieurs scans. Chaque fichier part au stockage privé tout de suite — rien ne transite par un formulaire.', 'Drag one or more scans. Each file goes to private storage at once — nothing travels through a form.')],
    [T('2. Indice de type', '2. Type hint'), T('Facultatif : dites au modèle ce que vous croyez que c’est. En cas de doute, laissez la détection automatique.', 'Optional: tell the model what you think it is. When in doubt, leave auto-detect.')],
    [T('3. Lancer', '3. Launch'), T('Tout le lot est lu en parallèle. La lecture ne modifie rien.', 'The whole batch is read in parallel. Reading changes nothing.')],
    [T('4. Ce qui a été lu', '4. What was read'), T('Personne, type, autorité, date d’émission, référence, nombre de pages. La date d’émission est lue telle quelle : si elle n’est pas lisible, elle reste vide.', 'Person, type, authority, issue date, reference, page count. The issue date is read literally: unreadable means empty.')],
    [T('5. Où ça va', '5. Where it goes'), T('Une demande d’acte, ou le Suivi de la Poste pour un courrier. Le modèle propose, vous décidez.', 'A civil-record request, or the Poste log for mail. The model proposes, you decide.')],
    [T('6. La cible', '6. The target'), T('Une correspondance sûre, sinon les candidats trouvés par nom, sinon la recherche dans toute la base, sinon créer la demande — avec ou sans dossier.', 'A certain match, else the candidates found by name, else a search across the whole base, else create the request — with or without a dossier.')],
    [T('7. Valider', '7. Approve'), T('Le document est attaché, jamais écrasé. Le statut est appliqué, l’expiration est calculée à partir de l’émission, et la réception est le jour du dépôt.', 'The document is attached, never overwritten. The status is applied, the expiry is derived from the issue date, and the reception is the day it was filed.')],
    [T('8. Qui est prévenu', '8. Who is told'), T('Les responsables du dossier. Sans dossier et sans responsable, tout le monde — pour que la bonne personne le reconnaisse et le rattache.', 'The dossier’s people. With no dossier and nobody responsible, everyone — so the right person recognises it and attaches it.')],
  ];
  return (
    <div className="actes-two">
      {steps.map(([h, p]) => (
        <div key={h} className="actes-block">
          <h3>{h}</h3>
          <p className="muted-note">{p}</p>
        </div>
      ))}
    </div>
  );
}
